import { useCallback, useEffect, useState, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { addMonths } from 'lib/datetime/dateUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import useFlightCalendarPrices from 'hooks/useFlightCalendarPrices'
import FlightsDatePickerDay from './FlightsDatePickerDay'
import { FlightsFareTypes } from 'constants/flight'
import { fireInteractionEvent } from 'api/googleTagManager'
import { flightsArrivalDateSelect, flightsDepartureDateSelect } from 'analytics/eventDefinitions'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import { StandaloneFlightMenu } from 'contexts/Flights/FlightSearchWidget/types'
import type { FlightSearchWidgetFlightItem } from 'contexts/Flights/FlightSearchWidget/types'
import type { DatePickerMonthChangeHandler } from 'components/Common/Calendar/DatePicker'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import VerticalDatePicker from 'components/Common/Calendar/VerticalDatePicker'
import {
  clampCalendarFocusMonth,
  resolveVerticalCalendarFocusDate,
} from 'components/Common/Calendar/calendarFocusMonth'
import truncateText from 'lib/string/truncateText'

interface Props {
  minDate?: Date
  maxDate?: Date
  className?: string
  id: string
  onDateChange?: (date: Date) => void
  featuredRanges?: Array<{
    start: Date
    end: Date
  }>
  required?: boolean
  mobileResultsRedesign?: boolean
  hideDayLabels?: boolean
  distinguishActiveLeg?: boolean
}

function getFlightName(airport?: App.AirportLocation) {
  return truncateText(airport?.cityDesc || airport?.airportName || '', 6, '', '..')
}

function FlightsVerticalDatePicker(props: Props) {
  const {
    minDate,
    maxDate,
    className,
    id,
    onDateChange,
    featuredRanges,
    required,
    mobileResultsRedesign,
    hideDayLabels,
    distinguishActiveLeg,
  } = props

  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)

  const prevFlight = useMemo(() => {
    const prevFlightIndex = state.flights.findIndex((flight) => flight.id === id)

    if (prevFlightIndex !== -1) {
      return state.flights[prevFlightIndex - 1]
    }
  }, [state.flights, id])

  const currentFlight = useMemo(() => {
    return state.flights.find((flight) => flight.id === id) || state.flights[0]
  }, [state.flights, id])

  const { fareType, fareCabin } = state

  const {
    checkinDate: startDate,
    checkoutDate: endDate,
    departureAirport,
    arrivalAirport,
  } = currentFlight

  const searchDispatch = useContext(FlightSearchWidgetDispatchContext)

  const isMultiCity = fareType?.value === FlightsFareTypes.MULTI_CITY

  const isReturn = fareType?.value === FlightsFareTypes.RETURN
  const isOneWay = fareType?.value === FlightsFareTypes.ONE_WAY

  const viewportFocusMonth = useMemo(() => {
    const selectedStart = startDate?.toDate() ?? prevFlight?.checkinDate?.toDate()
    return clampCalendarFocusMonth(
      resolveVerticalCalendarFocusDate(state.calendarFocusMonth, selectedStart),
      minDate,
      maxDate,
    )
  }, [maxDate, minDate, prevFlight?.checkinDate, startDate, state.calendarFocusMonth])

  const [date, setDate] = useState<Date>(() => (
    startDate?.toDate()
    ?? prevFlight?.checkinDate?.toDate()
    ?? clampCalendarFocusMonth(state.calendarFocusMonth, minDate, maxDate)
    ?? new Date()
  ))
  const viewportFocusTime = viewportFocusMonth?.getTime()

  useEffect(() => {
    if (startDate || viewportFocusTime === undefined) return
    setDate(new Date(viewportFocusTime))
  }, [startDate, viewportFocusTime])

  const defaultStartDate: Date = useMemo(() => {
    return minDate ?? new Date()
  }, [minDate])

  const defaultEndDate = useMemo(() => {
    return maxDate || addMonths(defaultStartDate, 12)
  }, [maxDate, defaultStartDate])

  const prices = useFlightCalendarPrices({
    departureAirportCode: departureAirport?.airportCode,
    arrivalAirportCode: arrivalAirport?.airportCode,
    fareType: fareType?.value,
    fareClass: fareCabin?.value,
    disabled: state.disableCalendarPricing,
    minDate: defaultStartDate,
    maxDate: defaultEndDate,
  })
  const { loading } = prices

  const earliestBookingDate = !loading && prices.earliestBookingDate ? new Date(prices.earliestBookingDate + 'T00:00:00') : minDate || defaultStartDate
  const finalBookingDate = !loading && prices.finalBookingDate ? new Date(prices.finalBookingDate) : maxDate || defaultEndDate

  const { selectingDateType, flights: stateFlights } = state

  const onDayClick = useCallback((selectedDay: Date) => {
    const day = moment(selectedDay)

    if ([FlightsFareTypes.ONE_WAY, FlightsFareTypes.MULTI_CITY].includes(fareType?.value as FlightsFareTypes)) {
      const selectedDayCachedPrice = prices.cachedPriceFor(day.format(ISO_DATE_FORMAT)) ?? 0
      const selectedDayPrice = Math.floor(selectedDayCachedPrice / 2)
      searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: day, quotedPrice: isOneWay ? selectedDayPrice : undefined })
      fireInteractionEvent(flightsDepartureDateSelect())
      searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu: undefined })

      // Clear next flights dates
      const currentFlightIndex = stateFlights.findIndex(flight => flight.id === id)
      for (let i = currentFlightIndex + 1; i < stateFlights.length; i++) {
        const flight = stateFlights[i]
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id: flight.id, date: null })
      }
    } else {
      const selectedDayCachedPrice = prices.cachedPriceFor(day.format(ISO_DATE_FORMAT)) ?? 0
      const selectedDayPrice = Math.floor(selectedDayCachedPrice / 2)

      // User explicitly clicked the "Returning" field and selected a valid return date
      if (selectingDateType === 'returning' && startDate && day.isSameOrAfter(startDate)) {
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: day, quotedPrice: selectedDayPrice })
        fireInteractionEvent(flightsArrivalDateSelect())
        searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id, activeMenu: undefined })
      // User explicitly clicked the "Departing" field - reset departure and clear return
      } else if (selectingDateType === 'departing') {
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: day, quotedPrice: selectedDayPrice })
        // Clear return date if it's now before the new departure date
        if (endDate && day.isAfter(endDate)) {
          searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: null, quotedPrice: null })
        }
        fireInteractionEvent(flightsDepartureDateSelect())
        // Move to selecting return date
        searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id, activeMenu: StandaloneFlightMenu.date, selectingDateType: 'returning' })
      // Fallback: original behavior for when selectingDateType is not set
      } else if (startDate && !endDate && day.isSameOrAfter(startDate)) {
        const departingCachedPrice = prices.cachedPriceFor(startDate.format(ISO_DATE_FORMAT)) ?? 0
        const departingPrice = Math.floor(departingCachedPrice / 2)
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: startDate, quotedPrice: departingPrice })
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: day, quotedPrice: selectedDayPrice })
        fireInteractionEvent(flightsArrivalDateSelect())
        searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id, activeMenu: undefined })
      } else {
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: day, quotedPrice: selectedDayPrice })
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: null, quotedPrice: null })
        fireInteractionEvent(flightsDepartureDateSelect())
      }
    }

    onDateChange?.(date)
  }, [fareType?.value, isOneWay, onDateChange, date, searchDispatch, id, startDate, endDate, stateFlights, selectingDateType, prices])

  const onMonthChange = useCallback<DatePickerMonthChangeHandler>((direction, date) => {
    setDate(date)
  }, [])

  const defaultMonth = useMemo(() => {
    if (prevFlight?.checkinDate) {
      return moment(prevFlight.checkinDate).get('month')
    }

    return date.getMonth()
  }, [date, prevFlight])

  const defaultYear = useMemo(() => {
    if (prevFlight?.checkinDate) {
      return moment(prevFlight.checkinDate).get('year')
    }

    return date.getFullYear()
  }, [date, prevFlight])

  const selectedFlightsByDate: Record<number, FlightSearchWidgetFlightItem> = useMemo(() => {
    return state.flights.reduce((dates, flight) => {
      if (flight.checkinDate) {
        return {
          ...dates,
          [flight.checkinDate.valueOf()]: flight,
        }
      }
      return dates
    }, {})
  }, [state.flights])

  const selectDateError = intl.formatMessage({
    id: 'GG1ljxkZ4Dp7SirA1NjY',
    defaultMessage: 'Please select a date',
    description: 'Error message when a flight date is not selected in the vertical date picker',
  })

  const dayRender = useCallback((day: Date, params: { disabled: boolean }) => {
    const { disabled } = params
    const dayTime = day.getTime()
    const dateOfDay = moment(day)
    const legOnDay = isMultiCity ? selectedFlightsByDate[dayTime] : undefined
    const isOtherLeg = !!distinguishActiveLeg && !!legOnDay && startDate?.valueOf() !== dayTime
    const selected = !isOtherLeg && (
      (startDate?.valueOf() === dayTime || endDate?.valueOf() === dayTime) ||
      (isMultiCity && !distinguishActiveLeg && Boolean(legOnDay))
    )
    const isDeparting = startDate?.valueOf() === dayTime
    const cachedPrice = !isMultiCity ? prices.cachedPriceFor(dateOfDay.format(ISO_DATE_FORMAT)) ?? 0 : 0
    const isInRange = (!selected && startDate && endDate) ? dateOfDay.isBetween(startDate, endDate) : false
    const perPersonPrice = Math.floor(cachedPrice / 2)
    const shouldCheckDate = prevFlight && fareType?.value === FlightsFareTypes.MULTI_CITY
    const isAfterLastFlight = shouldCheckDate ? dateOfDay.isAfter(prevFlight.checkinDate) : true
    const isFeatured = featuredRanges?.some(range => dateOfDay.isBetween(range.start, range.end, 'days', '[]'))
    const departingAirportName = getFlightName(currentFlight.departureAirport)
    const arrivalAirportName = getFlightName(currentFlight.arrivalAirport)
    const multiCityLabel = getFlightName(selectedFlightsByDate[dayTime]?.departureAirport)
    const defaultLabel = isDeparting ? departingAirportName : arrivalAirportName
    const label = isMultiCity ? multiCityLabel : defaultLabel
    return <FlightsDatePickerDay
      key={dayTime}
      day={day}
      data-testid={`day-${dateOfDay.format('YYYY-MM-DD')}`}
      selected={selected}
      resultsCalendar={mobileResultsRedesign}
      rangeStart={isReturn && !!endDate && !startDate?.isSame(endDate, 'day') && startDate?.valueOf() === dayTime}
      rangeEnd={isReturn && !!startDate && !startDate.isSame(endDate, 'day') && endDate?.valueOf() === dayTime}
      disabled={disabled || !isAfterLastFlight}
      price={prices.arePricesVisible ? perPersonPrice : undefined}
      loading={loading}
      isInRange={isInRange}
      onClick={onDayClick}
      cheapest={prices.isCheapestInMonth(dateOfDay.format(ISO_DATE_FORMAT))}
      featured={isFeatured}
      label={selected && !hideDayLabels ? label : undefined}
      otherLeg={isOtherLeg}
      legAirportCode={isOtherLeg ? legOnDay?.departureAirport?.airportCode : undefined}
    />
  }, [
    mobileResultsRedesign,
    isReturn,
    currentFlight.arrivalAirport,
    currentFlight.departureAirport,
    distinguishActiveLeg,
    endDate,
    fareType?.value,
    featuredRanges,
    hideDayLabels,
    prices,
    isMultiCity,
    loading,
    onDayClick,
    prevFlight,
    selectedFlightsByDate,
    startDate,
  ])

  return (
    <div className={className}>
      <HiddenInput
        name="startDate"
        value={startDate?.format(ISO_DATE_FORMAT)}
        required={required}
        invalidMessage={selectDateError}
      />

      <HiddenInput
        name="endDate"
        value={endDate?.format(ISO_DATE_FORMAT)}
        required={isReturn && required}
        invalidMessage={selectDateError}
      />

      <VerticalDatePicker
        longMonthNames={mobileResultsRedesign}
        min={earliestBookingDate}
        max={finalBookingDate}
        onMonthChange={onMonthChange}
        defaultMonth={defaultMonth}
        defaultYear={defaultYear}
        focusMonth={viewportFocusMonth}
        dayRender={dayRender}
        monthsGap={mobileResultsRedesign ? 48 : undefined}
        monthContentGap={mobileResultsRedesign ? 8 : undefined}
        hideWeekdays={mobileResultsRedesign}
      />
    </div>
  )
}

export default FlightsVerticalDatePicker
